import { lazy, Suspense, useEffect, useState } from 'react';
import { fetchMapCountries, fetchMapData } from '../../services/stockoutMapService';
import './stockoutMap.css';

const StockoutMapVisualization = lazy(() => import('./StockoutMapVisualization'));

export default function StockoutMap() {
  const [countries, setCountries] = useState([]);
  const [iso3, setIso3] = useState('');
  const [mapData, setMapData] = useState(null);
  const [countriesError, setCountriesError] = useState('');
  const [dataError, setDataError] = useState('');
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setCountriesLoading(true);
    setCountriesError('');
    fetchMapCountries(controller.signal).then((rows) => {
      if (controller.signal.aborted) return;
      setCountries(rows);
      setIso3((current) => rows.some((row) => row.iso3 === current) ? current : rows[0]?.iso3 || '');
      setCountriesLoading(false);
    }).catch((err) => {
      if (!controller.signal.aborted) { setCountriesError(err.message); setCountriesLoading(false); }
    });
    return () => controller.abort();
  }, [retry]);

  useEffect(() => {
    setMapData(null);
    setDataError('');
    if (!iso3) return undefined;
    const controller = new AbortController();
    setDataLoading(true);
    fetchMapData(iso3, controller.signal).then((value) => {
      if (!controller.signal.aborted) { setMapData(value); setDataLoading(false); }
    }).catch((err) => {
      if (!controller.signal.aborted) { setDataError(err.message); setDataLoading(false); }
    });
    return () => controller.abort();
  }, [iso3, retry]);

  const loading = countriesLoading || (Boolean(iso3) && dataLoading);
  const error = countriesError || dataError;

  return (
    <section className="stockout-map" aria-label="Stockout map">
      <div className="stockout-map-toolbar">
        <label>Country
          <select value={iso3} onChange={(event) => setIso3(event.target.value)} disabled={!countries.length}>
            {!countries.length && <option value="">No country boundaries</option>}
            {countries.map((country) => <option key={country.iso3} value={country.iso3}>{country.country_name}</option>)}
          </select>
        </label>
      </div>
      {error && <div role="alert"><p>{error}</p><button type="button" onClick={() => setRetry((value) => value + 1)}>Try again</button></div>}
      {loading && <p role="status">Loading stockout map…</p>}
      {!loading && !error && !countries.length && <p>No geographic areas are available yet.</p>}
      {!loading && !error && mapData && <Suspense fallback={<p role="status">Loading map…</p>}>
        <StockoutMapVisualization key={`${iso3}-${retry}`} data={mapData.data} geo={mapData.geo} />
        {mapData.data.meta.newStores > 0 && <p className="stockout-map-note stockout-map-unassigned" role="status">{mapData.data.meta.newStores} stores have no geographic assignment. Their records are included in the country total; update their store geography to place them on the map.</p>}
      </Suspense>}
    </section>
  );
}
